import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, Calendar, Activity, Download, Sun, CloudRain, Zap,
  BarChart3, Target, Heart, FileJson, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { todayLocal } from '../utils/dateUtils';

export const InsightsPage = ({ embedded = false }) => {
  const { API, token, user } = useAuth();
  const [moodTrends, setMoodTrends] = useState(null);
  const [symptomTrends, setSymptomTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [timeRange, setTimeRange] = useState(30);

  useEffect(() => {
    fetchTrends();
  }, [timeRange]);

  const fetchTrends = async () => {
    setLoading(true);
    try {
      const [moodRes, symptomRes] = await Promise.all([
        axios.get(`${API}/export/mood-trends?days=${timeRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/export/symptom-trends?days=${timeRange}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMoodTrends(moodRes.data);
      setSymptomTrends(symptomRes.data);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast.error('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (format = 'json') => {
    setExporting(true);
    try {
      if (format === 'csv') {
        // Direct download for CSV
        window.open(`${API}/export/csv?data_type=all`, '_blank');
        toast.success('CSV export started!');
      } else {
        const response = await axios.get(`${API}/export`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `calmpathways-export-${todayLocal()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Data exported successfully!');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getMoodColor = (status) => {
    switch (status) {
      case 'calm': return 'bg-green-500';
      case 'wobbly': return 'bg-amber-500';
      case 'intense': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getMoodIcon = (status) => {
    switch (status) {
      case 'calm': return <Sun className="w-4 h-4 text-green-600" />;
      case 'wobbly': return <CloudRain className="w-4 h-4 text-amber-600" />;
      case 'intense': return <Zap className="w-4 h-4 text-red-500" />;
      default: return null;
    }
  };

  const getSeverityColor = (severity) => {
    if (severity <= 3) return 'bg-green-500';
    if (severity <= 6) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-8" data-testid="insights-page">
      {/* Header - only show if not embedded */}
      {!embedded && (
        <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <TrendingUp className="w-12 h-12 text-primary" strokeWidth={1.5} />
                <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                  Insights & History
                </h1>
              </div>
              <p className="text-lg text-[#57534E] leading-relaxed">
                Track patterns and trends over time. Remember: this data is here to help, not to judge.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
              onClick={() => exportData('json')}
              disabled={exporting}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3 flex items-center gap-2"
              data-testid="export-json-button"
            >
              {exporting ? (
                <Download className="w-5 h-5 animate-bounce" />
              ) : (
                <FileJson className="w-5 h-5" />
              )}
              Export JSON
            </Button>
            <Button
              onClick={() => exportData('csv')}
              disabled={exporting}
              variant="outline"
              className="rounded-full px-6 py-3 flex items-center gap-2"
              data-testid="export-csv-button"
            >
              <Download className="w-5 h-5" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>
      )}

      {/* Time Range Selector */}
      <div className="flex items-center gap-4">
        <span className="text-[#57534E]">Time range:</span>
        <div className="flex gap-2">
          {[7, 14, 30, 60, 90].map((days) => (
            <Button
              key={days}
              onClick={() => setTimeRange(days)}
              variant={timeRange === days ? "default" : "outline"}
              className="rounded-full px-4"
              data-testid={`range-${days}`}
            >
              {days} days
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-[#A8A29E]">Loading your insights...</p>
        </div>
      ) : (
        <Tabs defaultValue="mood" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 bg-white rounded-xl border border-stone-200 p-2">
            <TabsTrigger value="mood" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Heart className="w-4 h-4 mr-2" />Mood Trends
            </TabsTrigger>
            <TabsTrigger value="symptoms" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Activity className="w-4 h-4 mr-2" />Symptom Patterns
            </TabsTrigger>
          </TabsList>

          {/* Mood Trends */}
          <TabsContent value="mood" className="space-y-6">
            {moodTrends && moodTrends.total_days > 0 ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-green-50 border-green-200 p-6 text-center">
                    <Sun className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-green-700">{moodTrends.summary.calm_days}</p>
                    <p className="text-sm text-green-600">Calm Days</p>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200 p-6 text-center">
                    <CloudRain className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-amber-700">{moodTrends.summary.wobbly_days}</p>
                    <p className="text-sm text-amber-600">Wobbly Days</p>
                  </Card>
                  <Card className="bg-red-50 border-red-200 p-6 text-center">
                    <Zap className="w-8 h-8 text-red-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-red-600">{moodTrends.summary.intense_days}</p>
                    <p className="text-sm text-red-500">Intense Days</p>
                  </Card>
                  <Card className="bg-primary/10 border-primary/30 p-6 text-center">
                    <Target className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-3xl font-bold text-primary">{moodTrends.best_calm_streak}</p>
                    <p className="text-sm text-primary/80">Best Calm Streak</p>
                  </Card>
                </div>

                {/* Visual Timeline */}
                <Card className="bg-white rounded-xl border border-stone-100 p-6">
                  <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Mood Timeline
                  </h3>
                  <div className="flex flex-wrap gap-1">
                    {moodTrends.trend_data.map((day, index) => (
                      <div
                        key={index}
                        className={`w-6 h-6 rounded ${getMoodColor(day.status)} ${day.has_journal ? 'ring-2 ring-primary ring-offset-1' : ''}`}
                        title={`${day.date}: ${day.status}${day.has_journal ? ' (journaled)' : ''}`}
                      />
                    ))}
                  </div>
                  <div className="flex items-center gap-6 mt-4 text-sm text-[#57534E]">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-green-500"></div>
                      <span>Calm</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-amber-500"></div>
                      <span>Wobbly</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-red-500"></div>
                      <span>Intense</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-gray-300 ring-2 ring-primary ring-offset-1"></div>
                      <span>Journaled</span>
                    </div>
                  </div>
                </Card>

                {/* Day of Week Patterns */}
                {Object.keys(moodTrends.day_patterns).length > 0 && (
                  <Card className="bg-white rounded-xl border border-stone-100 p-6">
                    <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Patterns by Day of Week
                    </h3>
                    <div className="grid grid-cols-7 gap-2">
                      {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => {
                        const value = moodTrends.day_patterns[day] || 0;
                        const height = value ? Math.max(20, (value / 3) * 100) : 20;
                        const bgColor = value <= 1.5 ? 'bg-green-400' : value <= 2.5 ? 'bg-amber-400' : 'bg-red-400';
                        return (
                          <div key={day} className="text-center">
                            <div className="h-24 flex items-end justify-center mb-2">
                              <div 
                                className={`w-8 ${bgColor} rounded-t transition-all`}
                                style={{ height: `${height}%` }}
                                title={`Average: ${value}`}
                              />
                            </div>
                            <span className="text-xs text-[#57534E]">{day}</span>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-sm text-[#A8A29E] mt-4 text-center">
                      Taller bars = more intense days on average. Notice any patterns?
                    </p>
                  </Card>
                )}

                {/* Encouragement */}
                <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-stone-100 p-6">
                  <div className="flex items-start gap-4">
                    <Heart className="w-8 h-8 text-primary flex-shrink-0" />
                    <div>
                      <h3 className="font-nunito font-bold text-[#2D2D2D] mb-2">Remember</h3>
                      <p className="text-[#57534E] leading-relaxed">
                        {moodTrends.summary.calm_days > moodTrends.summary.intense_days 
                          ? "You've had more calm days than intense ones. That's something to acknowledge."
                          : "Every day you've tracked is a day you showed up. That matters, regardless of what the day looked like."}
                      </p>
                    </div>
                  </div>
                </Card>
              </>
            ) : (
              <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
                <Calendar className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#57534E] text-lg mb-2">No mood data yet</p>
                <p className="text-[#A8A29E]">
                  Start checking in daily on the Dashboard to see your patterns here.
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Symptom Patterns */}
          <TabsContent value="symptoms" className="space-y-6">
            {symptomTrends && symptomTrends.total_entries > 0 ? (
              <>
                {/* Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-white rounded-xl border border-stone-100 p-6 text-center">
                    <Activity className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-3xl font-bold text-[#2D2D2D]">{symptomTrends.total_entries}</p>
                    <p className="text-sm text-[#57534E]">Entries Logged</p>
                  </Card>
                  <Card className="bg-white rounded-xl border border-stone-100 p-6 text-center">
                    <BarChart3 className="w-8 h-8 text-secondary-foreground mx-auto mb-2" />
                    <p className="text-3xl font-bold text-[#2D2D2D]">{symptomTrends.average_severity}/10</p>
                    <p className="text-sm text-[#57534E]">Avg Severity</p>
                  </Card>
                  <Card className="bg-white rounded-xl border border-stone-100 p-6 text-center">
                    <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-3xl font-bold text-[#2D2D2D]">{symptomTrends.top_symptoms.length}</p>
                    <p className="text-sm text-[#57534E]">Unique Symptoms</p>
                  </Card>
                </div>

                {/* Severity Timeline */}
                <Card className="bg-white rounded-xl border border-stone-100 p-6">
                  <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Severity Over Time
                  </h3>
                  <div className="flex items-end gap-1 h-32">
                    {symptomTrends.severity_trend.slice(0, 30).map((entry, index) => (
                      <div
                        key={index}
                        className={`flex-1 ${getSeverityColor(entry.severity)} rounded-t transition-all min-w-[8px]`}
                        style={{ height: `${entry.severity * 10}%` }}
                        title={`${entry.date}: Severity ${entry.severity}/10`}
                      />
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-[#A8A29E] mt-2">
                    <span>Older</span>
                    <span>Recent</span>
                  </div>
                </Card>

                {/* Top Symptoms and Triggers */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-white rounded-xl border border-stone-100 p-6">
                    <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4">Most Common Symptoms</h3>
                    {symptomTrends.top_symptoms.length > 0 ? (
                      <div className="space-y-3">
                        {symptomTrends.top_symptoms.map((symptom, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-[#57534E]">{symptom.name}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full"
                                  style={{ width: `${(symptom.count / symptomTrends.top_symptoms[0].count) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm text-[#A8A29E] w-8">{symptom.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#A8A29E]">No symptoms logged yet</p>
                    )}
                  </Card>

                  <Card className="bg-white rounded-xl border border-stone-100 p-6">
                    <h3 className="font-nunito font-bold text-[#2D2D2D] mb-4">Most Common Triggers</h3>
                    {symptomTrends.top_triggers.length > 0 ? (
                      <div className="space-y-3">
                        {symptomTrends.top_triggers.map((trigger, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-[#57534E]">{trigger.name}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-stone-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-secondary rounded-full"
                                  style={{ width: `${(trigger.count / symptomTrends.top_triggers[0].count) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm text-[#A8A29E] w-8">{trigger.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[#A8A29E]">No triggers logged yet</p>
                    )}
                  </Card>
                </div>
              </>
            ) : (
              <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
                <Activity className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
                <p className="text-[#57534E] text-lg mb-2">No symptom data yet</p>
                <p className="text-[#A8A29E]">
                  Start logging symptoms on the Symptom Tracker to see patterns here.
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default InsightsPage;
